import dayjs from 'dayjs';

import { MAX_CATEGORICAL_SERIES } from '@/components/common/chartPalette';
import type { PersonHours } from '@/components/project_details_components/HoursCard';

const round1 = (value: number): number => Math.round(value * 10) / 10;

// ---------------------------------------------------------------------------
// Graph 1 — User Utilization: each allocation period is plotted as its own bar,
// sized by that period's own utilization % = reportHours / allocationHours for
// that period (uncapped, so a single allocation can read past 100%). A person's
// overall totalUtilizationPercent (reportHours / totalAllocatedHours, used for
// the header text and the 100% over-allocation flag) is a separate figure —
// the chart recolors every bar in a row red for anyone over-allocated overall.
// ---------------------------------------------------------------------------

export interface UtilizationSegment {
  key: string; // matches a series dataKey — 'allocation1', 'allocation2', ... or 'allocationOther'
  label: string;
  allocationPercent: number;
  allocationHours: number;
  reportHours: number;
  allocationStartDate: string;
  allocationEndDate: string;
  /** This period's own utilization %, i.e. reportHours / allocationHours * 100. */
  contributionPercent: number;
}

export interface UtilizationChartRow {
  name: string;
  reportHours: number;
  totalAllocatedHours: number;
  totalUtilizationPercent: number;
  isOverAllocated: boolean;
  /** dataKey -> that allocation's own utilization %, read by the chart's Bar accessors. */
  values: Record<string, number>;
  segments: UtilizationSegment[];
  isActive?: number; // 1 = active, 0 = inactive
  lastWorkedDate?: string | null;
}

export interface UtilizationSeriesConfig {
  dataKey: string;
  name: string;
}

export interface UtilizationChartData {
  rows: UtilizationChartRow[];
  series: UtilizationSeriesConfig[];
}

export function buildUtilizationChartData(people: PersonHours[]): UtilizationChartData {
  const maxPeriods = people.reduce(
    (max, person) => Math.max(max, person.allocationDetails?.length ?? 0),
    0,
  );
  const seriesCount = Math.min(maxPeriods, MAX_CATEGORICAL_SERIES);

  const rows: UtilizationChartRow[] = people.map((person) => {
    const totalAllocatedHours = person.totalAllocatedHours ?? 0;
    const reportHours = person.hours;
    const totalUtilizationPercent =
      totalAllocatedHours > 0 ? round1((reportHours / totalAllocatedHours) * 100) : 0;

    // Periods beyond the palette limit fold into a shared 'allocationOther' key — accumulate raw
    // hours per key first so that bucket's utilization is a true reportHours/allocationHours
    // ratio across its folded periods, not an average of percentages.
    const hoursByKey: Record<string, { reportHours: number; allocationHours: number }> = {};
    const segments: UtilizationSegment[] = [];

    (person.allocationDetails ?? []).forEach((period, idx) => {
      const key = idx < seriesCount ? `allocation${idx + 1}` : 'allocationOther';
      const label = idx < seriesCount ? `Allocation ${idx + 1}` : 'Other';
      const periodUtilization =
        period.allocationHours > 0
          ? round1((period.reportHours / period.allocationHours) * 100)
          : 0;

      const bucket = hoursByKey[key] ?? { reportHours: 0, allocationHours: 0 };
      bucket.reportHours += period.reportHours;
      bucket.allocationHours += period.allocationHours;
      hoursByKey[key] = bucket;

      segments.push({
        key,
        label,
        allocationPercent: period.allocationPercent,
        allocationHours: period.allocationHours,
        reportHours: period.reportHours,
        allocationStartDate: period.allocationStartDate,
        allocationEndDate: period.allocationEndDate,
        contributionPercent: periodUtilization,
      });
    });

    const values: Record<string, number> = {};
    Object.entries(hoursByKey).forEach(([key, hours]) => {
      values[key] =
        hours.allocationHours > 0 ? round1((hours.reportHours / hours.allocationHours) * 100) : 0;
    });

    return {
      name: person.name,
      reportHours,
      totalAllocatedHours,
      totalUtilizationPercent,
      isOverAllocated: totalUtilizationPercent > 100,
      values,
      segments,
      isActive: person.isActive,
      lastWorkedDate: person.lastWorkedDate,
    };
  });

  const series: UtilizationSeriesConfig[] = Array.from({ length: seriesCount }, (_, idx) => ({
    dataKey: `allocation${idx + 1}`,
    name: `Allocation ${idx + 1}`,
  }));
  if (maxPeriods > seriesCount) {
    series.push({ dataKey: 'allocationOther', name: 'Other' });
  }

  return { rows, series };
}

// ---------------------------------------------------------------------------
// Graph 2 — User Allocation Timeline: each allocation period plotted as a
// floating [start, end] bar against real dates, one lane per period so gaps
// between periods stay visible. Lane count is unbounded (no palette-slot cap)
// since every lane renders in the same uniform color — position and row carry
// the meaning here, not hue.
// ---------------------------------------------------------------------------

export interface AllocationTimelineSegment {
  key: string; // 'allocation1', 'allocation2', ...
  allocationPercent: number;
  allocationHours: number;
  reportHours: number;
  allocationStartDate: string;
  allocationEndDate: string;
}

export interface AllocationTimelineRow {
  name: string;
  segments: AllocationTimelineSegment[];
  /** dataKey -> [startTimestamp, endTimestamp], read by the chart's floating-bar accessors. */
  values: Record<string, [number, number]>;
  isActive?: number; // 1 = active, 0 = inactive
  lastWorkedDate?: string | null;
}

export interface AllocationTimelineSeriesConfig {
  dataKey: string;
  name: string;
}

export interface AllocationTimelineData {
  rows: AllocationTimelineRow[];
  series: AllocationTimelineSeriesConfig[];
  /** Overall [min start, max end] timestamp across every allocation period, for the shared X domain. */
  domain: [number, number];
}

export function buildAllocationTimelineData(people: PersonHours[]): AllocationTimelineData {
  const maxPeriods = people.reduce(
    (max, person) => Math.max(max, person.allocationDetails?.length ?? 0),
    0,
  );

  let minTs = Infinity;
  let maxTs = -Infinity;

  const rows: AllocationTimelineRow[] = people.map((person) => {
    const values: Record<string, [number, number]> = {};
    const segments: AllocationTimelineSegment[] = [];

    (person.allocationDetails ?? []).forEach((period, idx) => {
      const start = dayjs(period.allocationStartDate).valueOf();
      const end = dayjs(period.allocationEndDate).valueOf();
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return; // gracefully skip unparsable dates instead of breaking the chart
      }
      const key = `allocation${idx + 1}`;
      values[key] = [start, end];
      minTs = Math.min(minTs, start);
      maxTs = Math.max(maxTs, end);

      segments.push({
        key,
        allocationPercent: period.allocationPercent,
        allocationHours: period.allocationHours,
        reportHours: period.reportHours,
        allocationStartDate: period.allocationStartDate,
        allocationEndDate: period.allocationEndDate,
      });
    });

    return {
      name: person.name,
      segments,
      values,
      isActive: person.isActive,
      lastWorkedDate: person.lastWorkedDate,
    };
  });

  const series: AllocationTimelineSeriesConfig[] = Array.from({ length: maxPeriods }, (_, idx) => ({
    dataKey: `allocation${idx + 1}`,
    name: `Allocation ${idx + 1}`,
  }));

  const domain: [number, number] =
    Number.isFinite(minTs) && Number.isFinite(maxTs) ? [minTs, maxTs] : [0, 0];

  return { rows, series, domain };
}
